import type { EmptyResponse } from '@/types/responseModel';
import type { CommonResponse } from '@/utils/request';
import { Request } from '@/utils/request';

export enum EConversationType {
  FRIEND = 'FRIEND',
  CIRCLE = 'CIRCLE',
  FRIEND_INVITATION = 'FRIEND_INVITATION',
  STRANGER = 'STRANGER'
}

export interface IBaseConversation {
  image: string;
  title: string;
  message: string;
  timestamp: string;
  unreadCount: number;
  muted: boolean;
  conversationType: EConversationType;
  importantMessage?: string;
}

export interface IFriendConversation extends IBaseConversation {
  conversationType: EConversationType.FRIEND;
  friend: {
    id: string;
    sessionId: string;
    route: string;
    label: string;
    remarks: string;
    relationRemarks: string;
    common: string;
    blockedByMe: boolean;
    isBlocked: boolean;
    autoReplySettings: IAutoReplySetting;
    relationType?: EFriendRelationType;
  };
}

export interface ICircleConversation extends IBaseConversation {
  conversationType: EConversationType.CIRCLE;
  circle: {
    route: string;
    sessionId: string;
    memberAvatars?: string[];
  };
}

export interface IInvitationConversation extends IBaseConversation {
  conversationType: EConversationType.FRIEND_INVITATION;
  invitation: {
    usernames: string[];
    totalNum: number;
  };
}

export interface IStrangerConversation extends IBaseConversation {
  conversationType: EConversationType.STRANGER;
  stranger: {
    usernames: string[];
    totalNum: number;
  };
}

export type IConversation =
  | IFriendConversation
  | ICircleConversation
  | IInvitationConversation
  | IStrangerConversation;

export interface IConversationList {
  total: number;
  totalUnreadCount: number;
  conversations: IConversation[];
}

export interface IFriendItem {
  friendId: string;
  name: string;
  avatar: string;
  sessionId: string;
  latestMessage: string;
  latestMessageTime: string;
  unreadCount: number;
  remarks: string;
  route: string;
  relationRemarks: string;
  common: string;
  blockedByMe: boolean;
  isBlocked: boolean;
  autoReplySettings?: IAutoReplySetting;
  relationType?: EFriendRelationType;
  conversationType?: EConversationType;

  // 前端专用
  isFriendRequestItem?: boolean;
  friendInvitation?: IFriendListFoldItem;
  strangerMessage?: IFriendListFoldItem;
  isCircleMessageItem?: boolean;
  circle?: {
    route: string;
  };
}

export interface IAutoReplySetting {
  enableMyAIAutoReply: boolean;
  enableOtherAIAutoReply: boolean;
  otherAIAutoReplyEnabled: boolean;
}
export interface IFriendRequestFromUserInfo {
  id: string;
  username: string;
  avatar: string;
  nickname?: string;
}

export interface IFriendRequestItem {
  fromUserInfo?: IFriendRequestFromUserInfo;
  targetUserInfo?: IFriendRequestFromUserInfo;
  message?: string;
  status?: EFriendRequestStatus;
  breakIceSession?: string;
  commons?: string[];
  commonFriendNum?: number;
  requestTime?: string;
  route?: string;
  source?: string;
}

export interface IFriendRequestList {
  list: IFriendRequestItem[];
  total: number;
}

export enum EFriendType {
  STRANGER = 'stranger',
  FRIEND = 'friend',
  DELETED = 'deleted',
  BLOCK = 'block'
}

export enum EFriendConfirmAction {
  ACCEPT = 'ACCEPTED',
  REJECT = 'REJECTED'
}

export enum EFriendRelationType {
  PENDING = 'pending',
  TWO_WAY_FRIEND = 'two-way-friend',
  ONE_WAY_FRIEND = 'one-way-friend'
}

export enum EFriendRequestStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired'
}

export interface IFriendListFoldItem {
  usernames?: string[];
  userNumber?: number;
  unreadCount?: number;
  latestMessageTime?: string;
  totalNum?: number;
}

export interface IFriendList {
  friends?: IFriendItem[];
  total: number;
  friendInvitation?: IFriendListFoldItem;
  strangerMessage?: IFriendListFoldItem;
  totalUnreadCount: number;
}

export interface IParticipantUserInfo extends IFriendRequestFromUserInfo {
  route: string;
  available: boolean;
}

export const getFriendList = (params?: { pageNo: number; pageSize: number; type: EFriendType }) => {
  return Request<CommonResponse<IFriendList>>({
    url: '/rest/os/friend/get/list',
    method: 'get',
    params: {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 999,
      type: params?.type || EFriendType.FRIEND
    }
  });
};

export const getFriendListV2 = (params?: {
  pageNo: number;
  pageSize: number;
  unreadOnly?: boolean;
}) => {
  return Request<CommonResponse<IConversationList>>({
    url: '/rest/os/conversation/list',
    method: 'get',
    params: {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 999,
      unreadOnly: params?.unreadOnly || false
    }
  });
};

export const removeConversationItem = (data: { type: EConversationType; uniqueKey: string }) => {
  return Request<EmptyResponse>({
    url: '/rest/os/conversation/item/delete',
    method: 'post',
    data
  });
};

export const getUnreadList = (params?: { pageNo: number; pageSize: number }) => {
  return Request<CommonResponse<IFriendList>>({
    url: '/rest/os/friend/get/unread/list',
    method: 'get',
    params: {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 999
    }
  });
};

export const getFriendDetail = (friendUserId: string) => {
  return Request<CommonResponse<IFriendItem>>({
    url: '/rest/os/friend/friend/detail',
    method: 'get',
    params: {
      friendUserId
    }
  });
};

export const getConversationDetail = (
  params:
    | {
        type: EConversationType;
        uniqueKey: string; // userId | circleRoute
      }
    | {
        type: EConversationType;
        sessionId: string;
      }
) => {
  const requestParams = {
    type: params.type,
    ...('uniqueKey' in params ? { uniqueKey: params.uniqueKey } : {}),
    ...('sessionId' in params ? { sessionId: params.sessionId } : {})
  };

  return Request<CommonResponse<IConversation>>({
    url: '/rest/os/conversation/item',
    method: 'get',
    params: {
      ...requestParams
    }
  });
};

export const getFriendRequestList = (params?: {
  pageNo: number;
  pageSize: number;
  order: string;
}) => {
  return Request<CommonResponse<{ list: IFriendRequestItem[]; total: number }>>({
    url: 'rest/os/friend/invite/list',
    method: 'get',
    params: {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 100,
      order: params?.order || 'desc'
    }
  });
};

export const deleteFriend = (data: { friendId: string }) => {
  return Request<EmptyResponse>({
    url: '/rest/os/friend/delete',
    method: 'post',
    data
  });
};

export const addFriend = (data: { friendId: string; sessionId?: string }) => {
  return Request<EmptyResponse>({
    url: '/rest/os/friend/add/oneway',
    method: 'post',
    data
  });
};
export const respondFriend = (data: { friendId: string; action: 'accept' | 'reject' }) => {
  return Request<EmptyResponse>({
    url: '/rest/os/friend/respond',
    method: 'post',
    data
  });
};

export const blockFriend = (data: { friendId: string }) => {
  return Request<EmptyResponse>({
    url: '/rest/os/friend/block',
    method: 'post',
    data
  });
};

export const unblockFriend = (data: { friendId: string }) => {
  return Request<EmptyResponse>({
    url: '/rest/os/friend/unblock',
    method: 'post',
    data
  });
};

export const sendFriendRequest = (data: {
  targetUserId: string;
  remark?: string;
  greeting?: string;
  source?: string;
  relationRemark?: string;
}) => {
  return Request<EmptyResponse>({
    url: '/rest/os/friend/invite/send',
    method: 'post',
    data
  });
};

export const confirmFriendRequest = (data: {
  targetUserId: string;
  action: EFriendConfirmAction;
  remark?: string;
  relationRemark?: string;
}) => {
  return Request<EmptyResponse>({
    url: '/rest/os/friend/invite/handle',
    method: 'post',
    data
  });
};

export const breakIce = (data: { friendId: string }) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/friend/break/ice',
    method: 'post',
    data
  });
};

export const joinGroup = (data: { sessionId: string }) => {
  return Request<EmptyResponse>({
    url: '/rest/general/session/add/session/member',
    method: 'post',
    data
  });
};

export const shareBot = (data: { description: string }) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/merchant/mebot/share',
    method: 'post',
    data
  });
};

export const clearBadge = (data: {
  businessType?: 'unread_message' | 'friend_request' | 'circle_feed';
  businessKey?: string; // sessionId|userId|route
}) => {
  return Request<EmptyResponse>({
    url: '/rest/os/notification/badge/clear',
    method: 'post',
    data
  });
};

export interface IInstructionTemplate {
  prompt: string;
  title: string;
}

export const getFriendTalkTemplateList = (lang?: 'zh' | 'en') => {
  return Request<CommonResponse<{ instructionTemplate: IInstructionTemplate[] }>>({
    url: '/rest/os/identity/output/friend/template/list',
    method: 'get',
    params: {
      ...(lang ? { lang: lang } : {})
    },
    headers: {
      ...(lang ? { lang: lang } : {})
    }
  });
};

export const getFriendMusicTemplateList = (lang?: 'zh' | 'en') => {
  return Request<CommonResponse<{ instructionTemplate: IInstructionTemplate[] }>>({
    url: '/rest/os/identity/output/music/template/list',
    method: 'get',
    params: {
      ...(lang ? { lang: lang } : {})
    },
    headers: {
      ...(lang ? { lang: lang } : {})
    }
  });
};

export const getFriendSnapTemplateList = (lang?: 'zh' | 'en') => {
  return Request<CommonResponse<{ instructionTemplate: IInstructionTemplate[] }>>({
    url: '/rest/os/duet/snap/prompt/list',
    method: 'get',
    params: {
      ...(lang ? { lang: lang } : {})
    },
    headers: {
      ...(lang ? { lang: lang } : {})
    }
  });
};

export const getSelector = (
  // 当 participantType 为 circle_friend 或 circle_stranger 时必填circleRoute
  params:
    | {
        participantType: 'friend';
        circleRoute?: string;
        checkTypes?: string[];
        pageNo?: number;
        pageSize?: number;
      }
    | {
        participantType: 'circle_friend' | 'circle_stranger';
        circleRoute: string;
        checkTypes?: string[];
        pageNo?: number;
        pageSize?: number;
      }
) => {
  return Request<CommonResponse<{ list: IParticipantUserInfo[]; total: number }>>({
    url: '/rest/os/friend/participant/selector',
    method: 'get',
    params: {
      ...params,
      checkTypes: params.checkTypes?.join(','),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 999
    }
  });
};

export enum EGroupRole {
  MEMBER = 'MEMBER',
  ADMIN = 'ADMIN',
  OWNER = 'OWNER'
}
export interface IGroupUserInfo extends IFriendRequestFromUserInfo {
  route: string;
  role: EGroupRole;
}

export const getCircleMemberList = (params: {
  route: string;
  pageNo?: number;
  pageSize?: number;
  adminOnly?: boolean;
}) => {
  return Request<CommonResponse<{ list: IGroupUserInfo[]; total: number }>>({
    url: '/rest/os/user/circle/member/list',
    method: 'get',
    params: {
      ...params,
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 999,
      adminOnly: params.adminOnly || false
    }
  });
};

export const createCircle = (data: { memberUserIds: string[] }) => {
  return Request<CommonResponse<EmptyResponse>>({
    url: '/rest/os/user/circle/create',
    method: 'post',
    data
  });
};

export interface ICircleAdminSettings {
  joinVerifyType: 'NO_VERIFY' | 'ANSWER_QUESTION';
  pendingApplyCount: number;
  joinQuestion?: string;
  adminCount: number;
  maxAdminCount: number;
}

export interface ICircleSettingDetail {
  route: string;
  name: string;
  icon: string;
  muted: boolean;
  description: string;
  sessionId: string;
  memberCount: number;
  members: IGroupUserInfo[];
  role: EGroupRole;
  nickname: string;
  circleRemarks: string;
  adminSettings: ICircleAdminSettings;
}

export const getCircleSettingDetail = (params: { route: string }) => {
  return Request<CommonResponse<ICircleSettingDetail>>({
    url: '/rest/os/user/circle/detail',
    method: 'get',
    params
  });
};

export const inviteToCircle = (data: {
  route: string;
  targetUserId?: string;
  targetUserIds?: string[];
}) => {
  return Request<CommonResponse<ICircleSettingDetail>>({
    url: '/rest/os/user/circle/invite',
    method: 'post',
    data
  });
};

export const quitCircle = (data: { route: string }) => {
  return Request<CommonResponse<boolean>>({
    url: '/rest/os/user/circle/quit',
    method: 'post',
    data
  });
};

export const updateCircleSetting = (data: {
  route: string;
  name?: string;
  icon?: string;
  description?: string;
  nickname?: string;
  circleRemarks?: string;
  joinVerifyType?: 'NO_VERIFY' | 'ANSWER_QUESTION';
  muted?: boolean;
  joinQuestion?: string;
}) => {
  return Request<CommonResponse<boolean>>({
    url: '/rest/os/user/circle/update',
    method: 'post',
    data
  });
};

export const dissolveCircle = (data: { route: string }) => {
  return Request<CommonResponse<boolean>>({
    url: '/rest/os/user/circle/dissolve',
    method: 'post',
    data
  });
};

export const transferCircleOwnership = (data: { route: string; targetUserId: string }) => {
  return Request<CommonResponse<boolean>>({
    url: '/rest/os/user/circle/owner/transfer',
    method: 'post',
    data
  });
};

export enum EOperationType {
  ADD = 'ADD',
  REMOVE = 'REMOVE'
}

export const circleAdminManage = (data: {
  route: string;
  operationType: EOperationType;
  userIds: string[];
}) => {
  return Request<CommonResponse<boolean>>({
    url: '/rest/os/user/circle/admin/manage',
    method: 'post',
    data
  });
};

export const circleMemberManage = (data: {
  route: string;
  operationType: EOperationType;
  userIds: string[];
}) => {
  return Request<CommonResponse<boolean>>({
    url: '/rest/os/user/circle/member/manage',
    method: 'post',
    data
  });
};

export enum EApplyStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  NO_VERIFY = 'NO_VERIFY'
}

export enum EApplyHistoryStatus {
  PENDING = 'PENDING',
  PROCESSED = 'PROCESSED'
}

export interface ICircleApplyItem {
  id: string;
  applicant: {
    id: string;
    username: string;
    avatar: string;
    route: string;
  };
  answer?: string;
  status: EApplyStatus;
  createTimestamp: string;
}

export interface ICircleApplyHistoryResponse {
  list: ICircleApplyItem[];
  total: number;
}

export const circleApplyHistory = (params: {
  route: string;
  status?: EApplyHistoryStatus;
  pageNo?: number;
  pageSize?: number;
}) => {
  return Request<CommonResponse<ICircleApplyHistoryResponse>>({
    url: '/rest/os/user/circle/apply/history',
    method: 'get',
    params: {
      ...params,
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 999
    }
  });
};

export enum EReviewOperationType {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT'
}

export const circleApplyReview = (data: {
  operationType: EReviewOperationType;
  applyIds: string[];
  rejectReason?: string;
}) => {
  return Request<CommonResponse<boolean>>({
    url: '/rest/os/user/circle/apply/review',
    method: 'post',
    data
  });
};

export const circleReport = (data: {
  route: string;
  reportType?: string;
  reportReason?: string;
  evidenceUrls?: string[];
}) => {
  return Request<CommonResponse<EmptyResponse>>({
    url: '/rest/os/report/circle/create',
    method: 'post',
    data
  });
};
